import { useParams, Link } from "react-router-dom";
import { useState, useMemo } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ComicCard from "@/components/base/ComicCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import { comics } from "@/mocks/comics";
import { categories, categoryMap } from "@/mocks/categories";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<"latest" | "hot" | "rating">("latest");

  const catName = categoryMap[slug || ""] || "全部";

  const filteredComics = useMemo(() => {
    let result = slug && slug !== "all" ? comics.filter((c) => c.category === slug) : [...comics];
    switch (sortBy) {
      case "hot":
        result.sort((a, b) => b.views - a.views);
        break;
      case "rating":
        result.sort((a, b) => b.rating - a.rating);
        break;
      case "latest":
      default:
        result.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
        break;
    }
    return result;
  }, [slug, sortBy]);

  const currentCat = categories.find((c) => c.slug === slug);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "首页", item: "https://www.4399mh.com/" },
                ...(slug && slug !== "all" ? [{ "@type": "ListItem", position: 2, name: catName, item: `https://www.4399mh.com/category/${slug}` }] : []),
              ],
            },
            {
              "@context": "https://schema.org",
              "@type": "WebPage",
              name: `${catName}韩漫 - 4399漫画网`,
              description: currentCat?.description || `浏览${catName}韩国漫画，免费无删减下拉式阅读`,
              url: `https://www.4399mh.com/category/${slug || "all"}`,
              isPartOf: { "@type": "WebSite", name: "4399漫画网", url: "https://www.4399mh.com" },
            },
          ]),
        }}
      />
      <Navbar />
      <main className="pt-16 min-h-screen">
        <div className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb items={[
              ...(slug && slug !== "all" ? [{ label: catName }] : []),
            ]} />

            <div className="pb-6">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                {catName}韩漫
              </h1>
              {currentCat?.description && (
                <p className="mt-2 text-sm text-foreground-500 max-w-2xl">
                  {currentCat.description}
                </p>
              )}
              <p className="mt-1 text-xs text-foreground-400">
                共 <strong className="text-primary-500">{filteredComics.length}</strong> 部作品
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6">
          {/* Category Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-6 scrollbar-hide">
            <div className="flex items-center bg-background-100 rounded-full p-1 flex-shrink-0">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    (slug === cat.slug) || (!slug && cat.slug === "all")
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-500 hover:text-foreground-700"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Sort Bar */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-foreground-500">
              筛选出 <strong className="text-foreground-700">{filteredComics.length}</strong> 部漫画
            </p>
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {([
                { key: "latest", label: "最新" },
                { key: "hot", label: "最热" },
                { key: "rating", label: "评分" },
              ] as const).map((item) => (
                <button
                  key={item.key}
                  onClick={() => setSortBy(item.key)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                    sortBy === item.key
                      ? "bg-accent-500 text-background-50"
                      : "text-foreground-500 hover:text-foreground-700"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {/* Comic Grid */}
          {filteredComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
              {filteredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-inbox-line text-2xl text-foreground-300"></i>
              </div>
              <p className="text-foreground-500">该分类暂无漫画</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}